import { rotationsTypes } from '@clickadilla/components/constants'
import guardSections from '@/types/guard-sections'
import routeNames from '@/types/route-names'
import { useCampaignsStore } from '@/store/campaigns/campaigns'

const getters = {
  getCasedFlatDealDefaultValueKey(state) {
    return state.flatDealDefaultValueKey.replace(/(_\w)/g, (match) => match[1].toUpperCase())
  },
  defaultUniquesTypes(state) {
    return {
      cap: state.defaultUniquesCap,
      ttl: state.defaultUniquesTtl
    }
  },
  getLocales(state) {
    return state.locales.filter(({ value }) => state.adNetwork.settings.locales.includes(value))
  },
  getMainRouteName(state) {
    if (state.adNetwork.guardSections.find(({ name }) => name === guardSections.DASHBOARD)) {
      return routeNames.DASHBOARD
    }
    if (state.adNetwork.guardSections.find(({ name }) => name === guardSections.RTB_STATISTICS)) {
      return routeNames.RTB_STATISTICS
    }
    return routeNames.PERSONAL
  },
  getAdNetworkImages(state) {
    return state.adNetwork.settings.images
  },
  helpVideosUrls(state) {
    return state.adNetwork.settings.helpVideosUrls
  },
  helpUrls(state) {
    return state.adNetwork.settings.helpUrls
  },
  maxImageSize(state) {
    return `${state.mediaFileSizeLimits['image/jpeg'] / 1024}KB`
  },
  maxVideoSize(state) {
    return `${state.mediaFileSizeLimits['video/mp4'] / (1024 * 1024)}MB`
  },
  maxZipSize(state) {
    return `${state.mediaFileSizeLimits['application/zip'] / (1024 * 1024)}MB`
  },
  getBannersSizes() {
    return [
      ...new Set(
        this.getAdFormatsByAdNetwork
          .filter((adFormat) => adFormat.type === 'banner')
          .map((adFormat) => adFormat.size)
      )
    ]
  },
  interstitialsSizes() {
    return this.getAdFormatsByAdNetwork
      .filter((adFormat) => adFormat.type === 'interstitial')
      .map((adFormat) => adFormat.size)
  },
  interstitialAdTypes(state) {
    return state.adTypes.interstitial || []
  },
  bannerAdTypes(state) {
    return state.adTypes.banner || []
  },
  inStreamAdTypes(state) {
    return state.adTypes.inStream || []
  },
  skipTimeOptions(state) {
    return state.inStreamAdSkipTimeOptions
  },
  getAdFormatsByAdNetwork(state) {
    const adFormatsFiltered = state.adNetwork.adFormats.sort((a, b) => {
      const value1 = state.adNetwork.settings.orderAdFormatTypes.findIndex(
        (value) => value === a.type
      )
      const value2 = state.adNetwork.settings.orderAdFormatTypes.findIndex(
        (value) => value === b.type
      )
      return value1 - value2
    })
    return adFormatsFiltered.map((adFormat) => ({
      ...adFormat,
      targetTypes: adFormat.targetTypes.filter(({ type }) =>
        state.adNetwork.targetTypes.some((targetType) => targetType.type === type)
      )
    }))
  },
  getChannelsByAdNetwork(state) {
    return {
      isCustomChannelsTitle: state.adNetwork.settings.channelsSettings.isCustomChannelsTitle,
      channels: state.adNetwork.settings.channelsSettings.channels.map((channel) => ({
        ...channel,
        adFormatsIds: channel.adFormatsIds.filter((adFormatId) =>
          this.getAdFormatsByAdNetwork.some(({ id }) => id === adFormatId)
        )
      }))
    }
  },
  getAdFormatTypesByAdNetwork() {
    return [...new Set(this.getAdFormatsByAdNetwork.map((adFormat) => adFormat.type))]
  },
  getRotationTypes(state) {
    const labels = { [rotationsTypes.SMART]: 'On', [rotationsTypes.RANDOM]: 'Off' }
    return state.rotationTypes.map((type) => ({ value: type, text: labels[type] || type }))
  },
  getTrafficProviderMethodTypes(state) {
    return state.trafficProviderMethodTypes.map((type) => {
      const foundProviderMethodType = state.providerMethodTypes.find((item) => item.value === type)
      return foundProviderMethodType ?? { value: type, text: type }
    })
  },
  getTrafficProviderPricingModels(state) {
    const { pricingModels } = useCampaignsStore()
    return state.trafficProviderPricingModels.map((type) => {
      const foundProviderMethodType = pricingModels.find(
        (item) => item.value === type
      )
      return foundProviderMethodType ?? { value: type, label: type }
    })
  },
  getFlatDealAdFormats(state) {
    return state.flatDealAdFormatIds.reduce((acc, id) => {
      const foundAdFormat = this.getAdFormatsByAdNetwork.find((adFormat) => adFormat.id === id)
      if (foundAdFormat) {
        acc.push(foundAdFormat)
      }
      return acc
    }, [])
  },
  rtbAdFormatGroups(state) {
    return state.adFormatGroups.filter((group) => group.isRtbHidden === 0)
  }
}

export default getters
